import React, { useCallback, useState, Fragment } from "react";
import { Input, Space, PortalItemType, useNavigation, buildURL } from "@thenounproject/lingo-core";

import ModalHeader from "../../../../app/_components/ModalHeader";
import ModalFooter from "../../../../app/_components/ModalFooter";
import ModalBody from "../../../../app/_components/ModalBody";

import useCreateKit from "@actions/kits/useCreateKit";
import useShowModal from "@redux/actions/useModals";
import useCreatePortalItems, {
  ItemData,
  InsertPosition,
} from "@redux/actions/portalItems/useCreatePortalItems";

type Props = {
  space: Space;
  portalId?: string;
  insertPosition?: InsertPosition;
};

const CreateKitModal: React.FC<Props> = ({ space, portalId, insertPosition }) => {
  const [kitName, setKitName] = useState(""),
    navigation = useNavigation(),
    { dismissModal } = useShowModal();

  const [createKit, { isProcessing, error }] = useCreateKit();

  const [addKitToPortal, { isProcessing: isProcessingPortal, error: portalError }] =
    useCreatePortalItems();

  const onKitCreated = useCallback(
    async (newKit: ItemData[]) => {
      const res = await addKitToPortal({ items: newKit, portalId, insertPosition });
      if (res.isSuccess) {
        dismissModal();
      }
    },
    [addKitToPortal, dismissModal, insertPosition, portalId]
  );

  const submit = useCallback(async () => {
    const name = kitName.trim();
    const res = await createKit({
      spaceId: space.id,
      name,
    });

    if (!res.isSuccess) return;

    const kits = res.response.entities?.kits;
    let kitId: string;
    if (portalId) {
      kitId = kits[res.response.result].kitId;
      const item: ItemData[] = [
        {
          type: PortalItemType.kit,
          kitId,
        },
      ];
      await onKitCreated(item);
    } else {
      kitId = kits[res.response.result].shortId;
      const kitUrl = buildURL(`/k/${kitId}`, { space }, { edit_section: "true" });
      navigation.push(kitUrl);
      dismissModal();
    }
  }, [createKit, dismissModal, navigation, kitName, space, portalId, onKitCreated]);

  return (
    <Fragment>
      <ModalHeader title="Create Kit" />
      <ModalBody>
        <Input
          id="kit-name-input"
          value={kitName}
          label="Kit Name"
          onChange={e => setKitName(e.target.value)}
          onSubmit={submit}
          placeholder="e.g. Style Guide, UI Library"
          message={error?.message || portalError?.message}
          inputStyle={error ? "error" : null}
          autoFocus
        />
      </ModalBody>
      <ModalFooter
        primary={{
          text: isProcessing || isProcessingPortal ? "Creating Kit..." : "Create Kit",
          onClick: submit,
          disabled: isProcessing || isProcessingPortal,
        }}
      />
    </Fragment>
  );
};

export default CreateKitModal;
